import { useEffect, useState } from "react";
import { auditApi, AuditLog } from "../api/audit";

function exportCsv(logs: AuditLog[]) {
  const headers = ["Time", "User", "Action", "Resource", "IP"];
  const rows = logs.map((log) => [
    new Date(log.created_at).toISOString(),
    log.user_email ?? "system",
    log.action,
    log.resource_type ? `${log.resource_type}/${log.resource_id}` : "",
    log.ip ?? "",
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `seavault-audit-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function actionBadge(action: string) {
  if (action.includes("upload") || action.includes("create"))
    return "bg-blue-100 text-blue-700";
  if (action.includes("download") || action.includes("deliver"))
    return "bg-green-100 text-green-700";
  if (action.includes("expir") || action.includes("delete") || action.includes("cancel"))
    return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-600";
}

export default function AuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);

  useEffect(() => {
    auditApi.list({ limit: 200 }).then(setLogs).catch(console.error);
  }, []);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Page header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Audit Log</h2>
          <p className="text-sm text-gray-500 mt-0.5">Complete event history · ISM/ISPS compliant</p>
        </div>
        <button
          onClick={() => exportCsv(logs)}
          disabled={logs.length === 0}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Time</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Resource</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-2.5 text-gray-500 text-xs font-mono whitespace-nowrap">
                    {new Date(log.created_at).toISOString().replace("T", " ").slice(0, 19)}
                  </td>
                  <td className="px-5 py-2.5 text-gray-700 text-xs">{log.user_email ?? "system"}</td>
                  <td className="px-5 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${actionBadge(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-gray-600 text-xs font-mono">
                    {log.resource_type ? `${log.resource_type}/${log.resource_id}` : "—"}
                  </td>
                  <td className="px-5 py-2.5 text-gray-500 text-xs">{log.ip ?? "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-gray-400">
                    No audit entries
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
